import { useState } from 'react';

import { ApiError } from '../../../shared/api/errors';
import { Banner } from '../../../shared/ui/layout';
import { Modal } from '../../../shared/ui/modal';
import { ByHandForm } from '../../preset-create';
import { EMPTY_SAMPLE, VerifyPanel, useDraftVerify, type ISampleDraft } from '../../preset-verify';
import type { IStagedSender } from '../use-bind-sender';
import { useHandBuilt } from '../use-hand-built';

interface IHandBuiltDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStaged: (staged: IStagedSender) => void;
  onBack?: () => void;
  /** The server refuses a second active secret, so the secret field is deliberately not asked for again. */
  hasSecret: boolean;
}

export const HandBuiltDialog = ({
  open,
  onOpenChange,
  onStaged,
  onBack,
  hasSecret,
}: IHandBuiltDialogProps) => {
  const hand = useHandBuilt();
  const [sample, setSample] = useState<ISampleDraft>(EMPTY_SAMPLE);
  const verify = useDraftVerify(hand.spec, sample, hand.secret);

  const close = () => {
    hand.reset();
    setSample(EMPTY_SAMPLE);
    onOpenChange(false);
  };

  const save = () =>
    hand.save.mutate(undefined, {
      onSuccess: (staged) => {
        onStaged(staged);
        close();
      },
    });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else if (onBack) onBack();
        else close();
      }}
      title="Set it up by hand"
      description="For a sender that is not in the catalogue but signs the ordinary way."
      width="wide"
      bleed
      cancelLabel={onBack ? 'Back' : 'Cancel'}
      submit={{
        label: 'Save',
        onSubmit: save,
        disabled: !hand.ready || hand.save.isPending,
        waiting: hand.save.isPending,
        waitingLabel: 'Saving',
      }}
    >
      <div className="flex gap-5 px-4 pt-1 pb-4">
        <div className="max-h-body min-w-0 flex-1 overflow-y-auto">
          <ByHandForm
            bare
            answers={hand.answers}
            onChange={hand.setAnswers}
            secret={hasSecret ? undefined : hand.secret}
            onSecretChange={hasSecret ? undefined : hand.setSecret}
          />
          {hand.save.isError && (
            <div className="px-4 pb-4">
              <Banner
                detail={hand.save.error instanceof ApiError ? hand.save.error.code : undefined}
              >
                {hand.save.error.message}
              </Banner>
            </div>
          )}
        </div>

        <div className="max-h-body w-panel-md shrink-0 overflow-y-auto">
          <VerifyPanel
            title="Checked as you type"
            spec={hand.spec}
            sample={sample}
            onSampleChange={setSample}
            secret={hand.secret}
            onSecretChange={hand.setSecret}
            secretElsewhere={!hasSecret}
            result={verify.result}
            error={verify.error}
            stale={verify.isStale}
            blocked={verify.blocked}
          />
        </div>
      </div>
    </Modal>
  );
};
